import {
  DOT_RADIUS,
  HOVER_DOT_RADIUS_PADDING,
  HOVER_MARKER_WIDTH,
  HOVER_MARKER_WIDTH_HALF,
  RANGE_MARKER_HEIGHT,
  X_AXIS_HEIGHT,
} from '../consts.ts';
import { RowData, TooltipState } from '../types.ts';
import { toPixel } from '../utils/chartDomain.ts';
import { theme } from '@aloft/toolkit-sui';

type HoverDotProps = {
  cx: number;
  cy: number;
  r: number;
  label: string;
  value: number;
  onHover: (t: TooltipState) => void;
};

const HoverDot = ({ cx, cy, r, label, value, onHover }: HoverDotProps) => (
  <circle
    cx={cx}
    cy={cy}
    r={r + HOVER_DOT_RADIUS_PADDING}
    fill="transparent"
    style={{ cursor: 'default' }}
    onMouseEnter={() => onHover({ label, value, x: cx, y: cy })}
    onMouseLeave={() => onHover(null)}
  />
);

type HoverMarkerProps = {
  x: number;
  y1: number;
  y2: number;
  label: string;
  value: number;
  onHover: (t: TooltipState) => void;
};

const HoverMarker = ({ x, y1, y2, label, value, onHover }: HoverMarkerProps) => (
  <rect
    x={x - HOVER_MARKER_WIDTH_HALF}
    y={y1}
    width={HOVER_MARKER_WIDTH}
    height={y2 - y1}
    fill="transparent"
    style={{ cursor: 'default' }}
    onMouseEnter={() => onHover({ label, value, x, y: (y1 + y2) / 2 })}
    onMouseLeave={() => onHover(null)}
  />
);

type RowLayerProps = {
  plotWidth: number;
  plotHeight: number;
  onHover: (t: TooltipState) => void;
  chartData: RowData[];
  minValue: number;
  maxValue: number;
};

export const ChartLayer = ({
  plotWidth,
  plotHeight,
  onHover,
  chartData,
  minValue,
  maxValue,
}: RowLayerProps) => {
  const usablePlotHeight = plotHeight - X_AXIS_HEIGHT;
  const rowHeight = usablePlotHeight / chartData.length;

  return (
    <>
      {chartData.map((row, i) => {
        const cy = usablePlotHeight - rowHeight * i - rowHeight / 2;
        const dotX = row.value
          ? toPixel(row.value, plotWidth, minValue, maxValue)
          : null;
        const avgX = row.average
          ? toPixel(row.average, plotWidth, minValue, maxValue)
          : null;
        const medX = row.median
          ? toPixel(row.median, plotWidth, minValue, maxValue)
          : null;
        const hasRange = row.min !== undefined && row.max !== undefined;
        const x1 =
          hasRange && row.min
            ? toPixel(row.min, plotWidth, minValue, maxValue)
            : null;
        const x2 =
          hasRange && row.max
            ? toPixel(row.max, plotWidth, minValue, maxValue)
            : null;

        return (
          <g key={row.type}>
            {hasRange && x1 !== null && x2 !== null && (
              <>
                <line x1={x1} y1={cy} x2={x2} y2={cy} stroke={theme.colors.alertInfoLight} strokeWidth={5} />
                <circle cx={x1} cy={cy} r={DOT_RADIUS} fill={theme.colors.graphChart} />
                <circle cx={x2} cy={cy} r={DOT_RADIUS} fill={theme.colors.graphChart} />
                {row.min && (
                  <HoverDot
                    cx={x1}
                    cy={cy}
                    r={DOT_RADIUS}
                    label="Min"
                    value={row.min}
                    onHover={onHover}
                  />
                )}
                {row.max && (
                  <HoverDot
                    cx={x2}
                    cy={cy}
                    r={DOT_RADIUS}
                    label="Max"
                    value={row.max}
                    onHover={onHover}
                  />
                )}
              </>
            )}

            {hasRange && (
              <>
                {medX !== null && (
                  <line
                    x1={medX}
                    y1={cy - RANGE_MARKER_HEIGHT}
                    x2={medX}
                    y2={cy + RANGE_MARKER_HEIGHT}
                    stroke={theme.colors.graphMedian}
                    strokeWidth={4}
                  />
                )}
                {row.median && medX !== null && (
                  <HoverMarker
                    x={medX}
                    y1={cy - RANGE_MARKER_HEIGHT}
                    y2={cy + RANGE_MARKER_HEIGHT}
                    label="Median"
                    value={row.median}
                    onHover={onHover}
                  />
                )}

                {avgX !== null && (
                  <line
                    x1={avgX}
                    y1={cy - RANGE_MARKER_HEIGHT}
                    x2={avgX}
                    y2={cy + RANGE_MARKER_HEIGHT}
                    stroke={theme.colors.graphAverage}
                    strokeWidth={4}
                  />
                )}
                {row.average && avgX !== null && (
                  <HoverMarker
                    x={avgX}
                    y1={cy - RANGE_MARKER_HEIGHT}
                    y2={cy + RANGE_MARKER_HEIGHT}
                    label="Average"
                    value={row.average}
                    onHover={onHover}
                  />
                )}
              </>
            )}

            {dotX !== null && !!row.value && (
              <>
                <circle cx={dotX} cy={cy} r={DOT_RADIUS} fill={theme.colors.graphChart} />
                <HoverDot
                  cx={dotX}
                  cy={cy}
                  r={DOT_RADIUS}
                  label={row.type}
                  value={row.value}
                  onHover={onHover}
                />
              </>
            )}
          </g>
        );
      })}
    </>
  );
};
